import React, { useRef } from 'react';
import type { TableRowSelection, FilterConfirmProps } from 'antd/lib/table/interface';
import { Table, Typography, Input, Space, Button, Skeleton, Pagination, Row, Col } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnType } from 'antd/lib/table';
import Highlighter from 'react-highlight-words';

import { useClientCopyConfigContext } from './ClientCopyConfigContext';
import { Client } from '../../types/Client';

type DataIndex = keyof Client;
const { Title } = Typography;

type Props = {
    setSelectedClients: (selectedClients: React.Key[]) => void;
    selectedClients: React.Key[];
};

const ClientCopyConfigList: React.FC<Props> = ({ setSelectedClients, selectedClients }): React.ReactElement => {
    const searchInput = useRef(null);
    const {
        isLoadingAllClients,
        allClients,
        isNoClientFound,
        searchedColumn,
        searchText,
        clientOffset,
        clientLimit,
        clientsCount,
        setSearchText,
        setSearchedColumn,
        setClientOffset,
    } = useClientCopyConfigContext();

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const onSelectChange = (newSelectedClients: React.Key[]) => {
        setSelectedClients(newSelectedClients);
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Client> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record): boolean => {
            if (
                record[dataIndex] !== undefined &&
                record !== undefined &&
                value !== undefined &&
                record[dataIndex] &&
                value
            ) {
                return Boolean(record[dataIndex]?.toString().toLowerCase().includes(String(value).toLowerCase()));
            }
            return false;
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const clientSelection: TableRowSelection<Client> = {
        selectedRowKeys: selectedClients,
        onChange: onSelectChange,
        selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            ...getColumnSearchProps('name'),
        },
        {
            title: 'stuartClientId',
            dataIndex: 'stuartClientId',
            key: 'stuartClientId',
            ...getColumnSearchProps('stuartClientId'),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            ...getColumnSearchProps('status'),
        },
    ];

    return (
        <>
            {isNoClientFound || isLoadingAllClients ? (
                <Skeleton active className="p-4" />
            ) : (
                <>
                    <Title level={5}>Choose the clients you&apos;d like to transfer the config to</Title>
                    <Table
                        dataSource={allClients}
                        columns={columns}
                        rowSelection={clientSelection}
                        rowKey="id"
                        pagination={false}
                    />
                    <Row justify="space-around" style={{ padding: '10px' }}>
                        <Col>
                            <Pagination
                                defaultCurrent={1}
                                current={Math.round(clientOffset / 100) + 1}
                                pageSize={clientLimit}
                                total={clientsCount}
                                showSizeChanger={false}
                                onChange={(page: number): void => setClientOffset((page - 1) * 100)}
                            />
                        </Col>
                    </Row>
                </>
            )}
        </>
    );
};

export default ClientCopyConfigList;
